import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { createEffect, createEvent, createStore, sample } from "effector";
import { appMounted } from "@features/app/model";
import { createFlagsClient, type FlagsClient, type FlagVariant } from "@features/flags-provider";
import { AnalyticsService } from "@features/shared/analytics";
import { setCoursesRemap, setLessonsRemap } from "@features/shared/request/model";
import { IUserAuthContract } from "@features/student/auth/model";
import { findLegalFlagKey, type LegalFlag } from "@features/legal-experiment";

type LessonsMapEdge = { source: number; target: number };
// courses_map experiment edge: target = original course replaced in listings,
// source = replacement course served in its place (backend mirrors progress back).
type CoursesMapEdge = { source: number; target: number };
// version: "call" = the old schedule-a-call flow, "ai" (default when the
// payload has no version field) = the AI-avatar interview flow.
export type RecordingOnInterviewVersion = "call" | "ai";
type RecordingOnInterviewPayload = {
    lesson_rating: number | number[];
    link: string;
    version: RecordingOnInterviewVersion;
};
type RequestIosReviewPayload = { lesson_id: number };

export type ProductOnboardingVariantPayload = { variant: string; lessonsMap: LessonsMapEdge[] };
export type ProductV3FlowVariant =
    | "guided_intro_lesson"
    | "challenge_100k_first_lesson"
    | "direct_challenge_entry";

export type StreakFlowVariant = "off" | "daily_goal" | "old";
export type BackToLearningVariant = "control" | "test_v1";
// New-landing A/B, flag `landing_v4_variant`: "control" = the current landing
// (LandingV3, the arm that won the retired `landing_v3_variant` A/B at 100%),
// "test" = the new v4 landing.
export type LandingV4Variant = "control" | "test";
// iOS start page (/app-onboarding/v2) design A/B, flag `apple_start_page`:
// "control" = the current look (AppleStartPageV1/V2), "test" = AppleStartPageV3
// (Claude certificate hero + LinkedIn card). Visual layer only — the funnel
// pair is still picked by `apple_start_page_variant`.
export type AppleStartPageDesignVariant = "control" | "test";
export type SubscriptionPageVariant = "control" | "test_v1";
// Cancel-flow offers A/B (steps 4–6): "control" = current screens, "test" =
// unified "Claim my offer" / "I don't want this offer" button copy on every
// offer step plus swapped buttons on step 6.
export type UnsubscribeOffersVariant = "control" | "test";
// Arm of the roadmap-recommendation experiment (flag key = the recommended
// roadmap's experiment_key, see features/roadmaps). "off" = client not
// bucketed / flag missing; null = not evaluated (user's funnel has no
// experiment-gated recommendation, so client.variant() was never called — no exposure).
export type RoadmapOnboardingExperimentArm = "treatment" | "control" | "off";
// PRD-015 A/B on the post-upsell product onboarding, flag `plan_ready_start_lesson`:
// "test" = a single "Your plan" screen whose CTA opens the first lesson of the
// recommended roadmap; "control" = the current two-step onboarding. "off" =
// client not bucketed / flag missing / provider failed; null = not evaluated
// (the student never met the onboarding launch conditions, so client.variant()
// was never called — no exposure). Everything except "test" renders control.
export type PlanReadyStartLessonArm = "test" | "control" | "off";
export const PLAN_READY_START_LESSON_FLAG_KEY = "plan_ready_start_lesson";

export const DEFAULT_STREAK_FLOW_VARIANT: StreakFlowVariant = "off";
export const DEFAULT_BACK_TO_LEARNING_VARIANT: BackToLearningVariant = "control";
export const DEFAULT_LANDING_V4_VARIANT: LandingV4Variant = "control";
export const DEFAULT_APPLE_START_PAGE_DESIGN_VARIANT: AppleStartPageDesignVariant = "control";
export const DEFAULT_SUBSCRIPTION_PAGE_VARIANT: SubscriptionPageVariant = "control";
export const DEFAULT_UNSUBSCRIBE_OFFERS_VARIANT: UnsubscribeOffersVariant = "control";

export const DEFAULT_PRODUCT_ONBOARDING_VARIANT: ProductOnboardingVariantPayload = {
    variant: "product_v3",
    lessonsMap: [{ source: 504, target: 383 }]
};

export const initializeExperiment = createEvent<FlagsClient | null>();
export const refreshFlags = createEvent();

const $experimentClient = createStore<FlagsClient | null>(null).on(initializeExperiment, (_, c) => c);
const $roadmapCertificateEnabled = createStore<boolean | null>(null);
// Gamification: XP, levels, level-up screen, next-lesson transition and sound effects.
const $gamificationEnabled = createStore<boolean | null>(null);
// Dark mode: shows the Light/Dark/Auto switcher and lets the theme model apply the .dark class.
const $darkModeEnabled = createStore<boolean | null>(null);
const $onboardingChallengeRedirectEnabled = createStore<boolean | null>(null);
const $trustPilotEnabled = createStore<boolean | null>(null);
const $lessonFeedbackStarsEnabled = createStore<boolean | null>(null);
const $lessonsMap = createStore<LessonsMapEdge[]>([]);
const $coursesMap = createStore<CoursesMapEdge[]>([]);
const $unsubscribeStep4Enabled = createStore<boolean | null>(null);
const $unsubscribeStep5DiscountOfferEnabled = createStore<boolean | null>(null);
// Cancel-flow offers A/B (see UnsubscribeOffersVariant).
// null = not evaluated yet (lazy, see resolveUnsubscribeOffersVariantFx).
const $unsubscribeOffersVariant = createStore<UnsubscribeOffersVariant | null>(null);
// A/B experiment for the AI-tools upsell page redesign. Variant value "test" =
// new variant (`AiToolsV2`); "control" or `null` (flag off / not bucketed) = `AiTools`.
const $aiToolsUpsellVariant = createStore<string | null>(null);
const $appleStartPageVariant = createStore<string | null>(null);
const $appleStartPageDesignVariant = createStore<AppleStartPageDesignVariant>(DEFAULT_APPLE_START_PAGE_DESIGN_VARIANT);
const $recordingOnInterview = createStore<RecordingOnInterviewPayload | null>(null);
const $requestIosReview = createStore<RequestIosReviewPayload | null>(null);
const $productOnboardingVariant = createStore<ProductOnboardingVariantPayload>(DEFAULT_PRODUCT_ONBOARDING_VARIANT);
const $pushNotificationTriggerLessonIds = createStore<string | null>(null);
const $backToLearningVariant = createStore<BackToLearningVariant>(DEFAULT_BACK_TO_LEARNING_VARIANT);
const $notificationsSettingsEnabled = createStore<boolean>(false);
const $streakFlow = createStore<StreakFlowVariant>(DEFAULT_STREAK_FLOW_VARIANT);
const $landingV4Variant = createStore<LandingV4Variant>(DEFAULT_LANDING_V4_VARIANT);
const $subscriptionPageVariant = createStore<SubscriptionPageVariant>(DEFAULT_SUBSCRIPTION_PAGE_VARIANT);
const $roadmapOnboardingExperimentArm = createStore<RoadmapOnboardingExperimentArm | null>(null);
// null = not evaluated yet (see the lazy-exposure comment at resolvePlanReadyStartLessonArmFx)
const $planReadyStartLessonArm = createStore<PlanReadyStartLessonArm | null>(null);
// null = not evaluated yet (see the lazy-exposure comment at resolveAppTourEnabledFx)
const $appTourEnabled = createStore<boolean | null>(null);
// null = not evaluated yet (the /ai-tools page keeps a skeleton in its slots).
const $aiToolsProfessionSetEnabled = createStore<boolean | null>(null);
const $downloadInvoiceEnabled = createStore<boolean>(false);
/**
 * Localize feature flag — hardcoded toggle.
 * Set to `true` to enable Localize translation service, `false` to disable.
 * When disabled: Localize script is not loaded, language picker is hidden.
 */
const $localizeEnabled = createStore<boolean>(false);

const initExperimentFx = createEffect(async (): Promise<FlagsClient | null> => {
    if (typeof window === "undefined") return null;
    // PostHog-backed client, see features/flags-provider. The rest of this
    // model is provider-agnostic.
    return createFlagsClient();
});

sample({
    clock: appMounted,
    target: initExperimentFx
});

sample({
    clock: initExperimentFx.doneData,
    target: initializeExperiment
});

const fetchFlagsFx = createEffect(async (client: FlagsClient | null) => {
    if (!client) return false;
    await client.fetch();
    return true;
});

// IMPORTANT: keep this effect LAST in the compute-effects list below.
// It's used as a "barrier" to mark the moment when all flag compute effects have finished.
const signalFlagsReadyFx = createEffect(() => { });

const $appleStartPageFlagsReady = createStore<boolean>(false)
    .on(refreshFlags, () => false)
    .on(fetchFlagsFx, () => false);

const $flagsLoaded = createStore<boolean>(false)
    .on(refreshFlags, () => false)
    .on(fetchFlagsFx, () => false);

// true once the analytics user identity for this session is set (login or
// session restore); reset on logout. Wired in AmplitudeFlagsModule.run().
const $userIdentified = createStore<boolean>(false);

// true when the last completed flag fetch ran under the signed-in identity.
// This is the only safe moment to evaluate signed-in-only flags: bucketing is
// by distinct_id, so evaluating earlier buckets by the throwaway anonymous
// device identity — the variant re-rolls on every clean session (courses_map
// A/B flips) and $exposure lands on users who can never see the feature
// (back_to_learning ~23% variant jumping, anonymous exposures).
const $identityFlagsReady = createStore<boolean>(false)
    .on(refreshFlags, () => false)
    .on(fetchFlagsFx, () => false);

// Latched variant of the above for UI/data gating: true from the moment the
// FIRST signed-in flag cycle has completed AND the courses remap header has
// been applied — the earliest point where course lists can be fetched with
// the correct X-Course-Remap header. Unlike $identityFlagsReady it does NOT
// reset on refetches (no loader flashes mid-session); it resets on logout,
// and is released immediately when no experiment client exists.
const $identityFlagsSettled = createStore<boolean>(false);

const TRUE_FLAG_VALUES = new Set(["1", "true", "on", "enabled", "yes", "active"]);
const FALSE_FLAG_VALUES = new Set(["0", "false", "off", "disabled", "no", "inactive"]);

const normalizeStringValue = (value: unknown): string | null => {
    if (typeof value !== "string") return null;
    const normalized = value.trim().toLowerCase();
    return normalized.length > 0 ? normalized : null;
};

const parseBooleanLike = (value: unknown): boolean | null => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number" && Number.isFinite(value)) {
        if (value === 1) return true;
        if (value === 0) return false;
    }

    const normalized = normalizeStringValue(value);
    if (!normalized) return null;
    if (TRUE_FLAG_VALUES.has(normalized)) return true;
    if (FALSE_FLAG_VALUES.has(normalized)) return false;
    return null;
};

type BooleanFlagValueGetter = (variant: FlagVariant | null) => unknown;

const createBooleanFlagEffect = ({
    key,
    fallback = null,
    pickValues = [(variant) => variant?.value]
}: {
    key: string;
    fallback?: boolean | null;
    pickValues?: BooleanFlagValueGetter[];
}) =>
    createEffect((client: FlagsClient | null): boolean | null => {
        if (!client) return fallback;
        const variant = client.variant(key);

        for (const pickValue of pickValues) {
            const parsed = parseBooleanLike(pickValue(variant));
            if (parsed !== null) return parsed;
        }

        return fallback;
    });

// Gates the new roadmap certificate program block (and hides the old roadmap hero).
// "off"/"disable" => disabled (default), "enable" => enabled.
const computeRoadmapCertificateEnabledFx = createBooleanFlagEffect({ key: "roadmap_certificate", fallback: false });
const computeGamificationEnabledFx = createBooleanFlagEffect({ key: "gamification_enabled", fallback: false });
const computeDarkModeEnabledFx = createBooleanFlagEffect({ key: "dark_mode_enabled", fallback: false });

const computeOnboardingChallengeRedirectEnabledFx = createBooleanFlagEffect({
    key: "onboarding_challenge_redirect_enabled"
});

const computeTrustPilotEnabledFx = createBooleanFlagEffect({
    key: "trustpilot_enabled",
    pickValues: [
        (variant) => variant?.value,
        (variant) => variant?.payload?.value,
        (variant) => variant?.payload?.enabled
    ]
});

const computeLessonFeedbackStarsEnabledFx = createBooleanFlagEffect({
    key: "lesson_feedback_stars",
    pickValues: [
        (variant) => variant?.value,
        (variant) => variant?.payload?.value,
        (variant) => variant?.key,
        (variant) => variant?.payload?.key
    ]
});

const computeUnsubscribeStep4EnabledFx = createBooleanFlagEffect({
    key: "unsubscribe_step4_gift_offer_enabled"
});

const computeUnsubscribeStep5DiscountOfferEnabledFx = createBooleanFlagEffect({
    key: "unsubscribe_step5_discount_offer_enabled"
});

const computeLessonsMapFx = createEffect((client: FlagsClient | null) => {
    if (!client) return [] as LessonsMapEdge[];
    const variant = client.variant("lessons_map");
    const payload: any = (variant as any)?.payload;

    return payload || [];
});

const computeCoursesMapFx = createEffect((client: FlagsClient | null) => {
    if (!client) return [] as CoursesMapEdge[];
    const variant = client.variant("courses_map");
    const payload: any = (variant as any)?.payload;

    return payload || [];
});

const computeProductOnboardingVariantFx = createEffect((client: FlagsClient | null) => {
    if (!client) return null as ProductOnboardingVariantPayload | null;
    const v = client.variant("product_onboarding_variant");
    const value: any = v?.value ?? (v as any)?.key ?? (v as any)?.payload?.value;
    const rawPayload: any = (v as any)?.payload;
    if (typeof value !== "string") return null;
    const variant = value.trim();
    const arr = Array.isArray(rawPayload) ? rawPayload : [];
    const lessonsMap = arr.filter(
        (e: any) => typeof e?.source === "number" && typeof e?.target === "number"
    ) as LessonsMapEdge[];
    return { variant, lessonsMap };
});

const BACK_TO_LEARNING_VARIANTS: ReadonlySet<BackToLearningVariant> = new Set(["control", "test_v1"]);

const computeBackToLearningVariantFx = createEffect((client: FlagsClient | null): BackToLearningVariant => {
    if (!client) return DEFAULT_BACK_TO_LEARNING_VARIANT;

    const variant = client.variant("back_to_learning_variant");
    const value: any = variant?.value ?? (variant as any)?.key ?? (variant as any)?.payload?.value;
    const normalized = typeof value === "string" ? value.trim().toLowerCase() : "";

    if (BACK_TO_LEARNING_VARIANTS.has(normalized as BackToLearningVariant)) {
        return normalized as BackToLearningVariant;
    }

    return DEFAULT_BACK_TO_LEARNING_VARIANT;
});

const computeNotificationsSettingsEnabledFx = createBooleanFlagEffect({
    key: "notifications_settings_enabled",
    fallback: false
});

const computeDownloadInvoiceEnabledFx = createBooleanFlagEffect({
    key: "download_invoice_enabled",
    fallback: false
});

const STREAK_FLOW_VARIANTS: ReadonlySet<StreakFlowVariant> = new Set(["off", "daily_goal", "old"]);

const LANDING_V4_VARIANTS: ReadonlySet<LandingV4Variant> = new Set(["control", "test"]);

const SUBSCRIPTION_PAGE_VARIANTS: ReadonlySet<SubscriptionPageVariant> = new Set(["control", "test_v1"]);

const computeStreakFlowFx = createEffect((client: FlagsClient | null): StreakFlowVariant => {
    if (!client) return DEFAULT_STREAK_FLOW_VARIANT;

    const variant = client.variant("streak_flow");
    const value: any = variant?.value ?? (variant as any)?.key ?? (variant as any)?.payload?.value;
    const normalized = typeof value === "string" ? value.trim().toLowerCase() : "";

    if (STREAK_FLOW_VARIANTS.has(normalized as StreakFlowVariant)) {
        return normalized as StreakFlowVariant;
    }

    return DEFAULT_STREAK_FLOW_VARIANT;
});

const computeLandingV4VariantFx = createEffect((client: FlagsClient | null): LandingV4Variant => {
    if (!client) return DEFAULT_LANDING_V4_VARIANT;

    const variant = client.variant("landing_v4_variant");
    const value: any = variant?.value ?? (variant as any)?.key ?? (variant as any)?.payload?.value;
    const normalized = typeof value === "string" ? value.trim().toLowerCase() : "";

    if (LANDING_V4_VARIANTS.has(normalized as LandingV4Variant)) {
        return normalized as LandingV4Variant;
    }

    return DEFAULT_LANDING_V4_VARIANT;
});

const computeSubscriptionPageVariantFx = createEffect((client: FlagsClient | null): SubscriptionPageVariant => {
    if (!client) return DEFAULT_SUBSCRIPTION_PAGE_VARIANT;

    const variant = client.variant("subscription_page");
    const value: any = variant?.value ?? (variant as any)?.key ?? (variant as any)?.payload?.value;
    const normalized = typeof value === "string" ? value.trim().toLowerCase() : "";

    if (SUBSCRIPTION_PAGE_VARIANTS.has(normalized as SubscriptionPageVariant)) {
        return normalized as SubscriptionPageVariant;
    }

    return DEFAULT_SUBSCRIPTION_PAGE_VARIANT;
});

const computeAiToolsUpsellVariantFx = createEffect((client: FlagsClient | null) => {
    if (!client) return null as string | null;
    const variant = client.variant("ai_tools_upsell_variant");
    const value: any = variant?.value ?? (variant as any)?.payload?.value;
    if (typeof value === "string") {
        const normalized = value.trim();
        if (normalized.length > 0) return normalized;
    }
    return null;
});

// Mirror the resolved AI-tools-upsell A/B group onto the user profile as a
// user property (goes to BOTH analytics systems via the dual-write wrapper).
// The purchase event ("upsell_purchase_completed_success") is fired server-side
// from a webhook (no request context, variant not stored), but under the same
// user_id ("student_<id>"), so segmenting that event by this user property
// works without any backend change. Set for BOTH "test" and "control" —
// otherwise the control cohort can't be measured.
const setAiToolsUpsellVariantUserPropFx = createEffect((variant: string) => {
    if (typeof window === "undefined") return;
    AnalyticsService.setUserProps({ ai_tools_upsell_variant: variant });
});

// Roadmap-recommendation experiment (see features/roadmaps). The flag key is
// dynamic — it is the experiment_key of the roadmap recommended for the
// student's funnel — and the evaluation is DELIBERATELY lazy: client.variant()
// auto-tracks the exposure event, so it must run only for students whose
// funnel actually has an experiment-gated recommendation. That keeps the
// exposed population in analytics equal to the eligible funnels. Do NOT add
// this to the eager compute fan-out.
export const roadmapOnboardingExperimentRequested = createEvent<string>();

const resolveRoadmapOnboardingExperimentFx = createEffect(
    ({
        client,
        experimentKey
    }: {
        client: FlagsClient | null;
        experimentKey: string;
    }): RoadmapOnboardingExperimentArm => {
        if (!client || !experimentKey) return "off";
        const variant = client.variant(experimentKey);
        const value: any = variant?.value ?? (variant as any)?.key ?? (variant as any)?.payload?.value;
        const normalized = typeof value === "string" ? value.trim().toLowerCase() : "";
        if (normalized === "treatment" || normalized === "test") return "treatment";
        if (normalized === "control") return "control";
        return "off";
    }
);

// Same server-side-events rationale as ai_tools_upsell_variant above: mirror
// the arm for both groups so webhook-fired purchase events stay segmentable.
const setRoadmapOnboardingExperimentUserPropFx = createEffect((arm: RoadmapOnboardingExperimentArm) => {
    if (typeof window === "undefined") return;
    AnalyticsService.setUserProps({ roadmaps_for_onboardings_variant: arm });
});

sample({
    clock: roadmapOnboardingExperimentRequested,
    source: $experimentClient,
    fn: (client, experimentKey) => ({ client, experimentKey }),
    target: resolveRoadmapOnboardingExperimentFx
});

sample({
    clock: resolveRoadmapOnboardingExperimentFx.doneData,
    target: $roadmapOnboardingExperimentArm
});

sample({
    clock: resolveRoadmapOnboardingExperimentFx.doneData,
    filter: (arm) => arm !== "off",
    target: setRoadmapOnboardingExperimentUserPropFx
});

// App tour (guided walkthrough shown once after the product onboarding).
// Evaluated lazily for the same reason as the roadmap experiment above:
// client.variant() auto-tracks $exposure, and the exposed population must be
// only the users who actually met the tour launch conditions. The app-tour
// model fires this event at that moment. Do NOT add this to the eager fan-out.
export const appTourFlagRequested = createEvent();

const resolveAppTourEnabledFx = createEffect((client: FlagsClient | null): boolean => {
    if (!client) return false;
    const variant = client.variant("app_tour_enabled");

    for (const pickValue of [variant?.value, (variant as any)?.payload?.value, (variant as any)?.key]) {
        const parsed = parseBooleanLike(pickValue);
        if (parsed !== null) return parsed;
    }

    return false;
});

sample({
    clock: appTourFlagRequested,
    source: $experimentClient,
    target: resolveAppTourEnabledFx
});

sample({
    clock: resolveAppTourEnabledFx.doneData,
    target: $appTourEnabled
});

// Cancel-flow offers A/B. Evaluated lazily: the unsubscribe wizard fires this
// event the moment an offer step (4–6) is actually displayed, so $exposure lands
// only on users who saw at least one offer screen. Do NOT add this to the eager fan-out.
export const unsubscribeOffersVariantRequested = createEvent();

const UNSUBSCRIBE_OFFERS_VARIANTS: ReadonlySet<UnsubscribeOffersVariant> = new Set(["control", "test"]);

const resolveUnsubscribeOffersVariantFx = createEffect((client: FlagsClient | null): UnsubscribeOffersVariant => {
    if (!client) return DEFAULT_UNSUBSCRIBE_OFFERS_VARIANT;

    const variant = client.variant("unsubscribe_offers_variant");
    const value: any = variant?.value ?? (variant as any)?.key ?? (variant as any)?.payload?.value;
    const normalized = typeof value === "string" ? value.trim().toLowerCase() : "";

    if (UNSUBSCRIBE_OFFERS_VARIANTS.has(normalized as UnsubscribeOffersVariant)) {
        return normalized as UnsubscribeOffersVariant;
    }

    return DEFAULT_UNSUBSCRIBE_OFFERS_VARIANT;
});

sample({
    clock: unsubscribeOffersVariantRequested,
    source: $experimentClient,
    target: resolveUnsubscribeOffersVariantFx
});

sample({
    clock: resolveUnsubscribeOffersVariantFx.doneData,
    target: $unsubscribeOffersVariant
});

// Profession task cards on /ai-tools (features/ai-tools/profession-cards). Evaluated
// lazily — the page fires the request on mount — so $feature_flag_called lands only
// on users who opened /ai-tools. Re-evaluated after every flag refetch like the
// roadmap experiment above. Do NOT add this to the eager fan-out.
export const aiToolsProfessionSetRequested = createEvent();
const $aiToolsProfessionSetRequested = createStore(false).on(aiToolsProfessionSetRequested, () => true);

const resolveAiToolsProfessionSetEnabledFx = createBooleanFlagEffect({
    key: "ai_tools_profession_set_enabled",
    fallback: false
});

// Mirrored for both arms so any event can be segmented by the arm.
const setAiToolsProfessionSetUserPropFx = createEffect((enabled: boolean | null) => {
    if (typeof window === "undefined") return;
    AnalyticsService.setUserProps({ ai_tools_profession_set_enabled: enabled });
});

sample({
    clock: [$aiToolsProfessionSetRequested.updates, $identityFlagsReady.updates],
    source: { client: $experimentClient, ready: $identityFlagsReady, requested: $aiToolsProfessionSetRequested },
    filter: ({ ready, requested }) => ready && requested,
    fn: ({ client }) => client,
    target: resolveAiToolsProfessionSetEnabledFx
});

sample({
    clock: resolveAiToolsProfessionSetEnabledFx.doneData,
    target: [$aiToolsProfessionSetEnabled, setAiToolsProfessionSetUserPropFx]
});

// PRD-015 (see PlanReadyStartLessonArm). Evaluated lazily for the same reason
// as the roadmap experiment above: client.variant() auto-tracks the exposure,
// and the exposed population must be exactly the web students who met the
// product-onboarding launch conditions (= reached the end of the upsell chain).
// The product-onboarding model fires the request at that moment
// (features/onboarding/ui/productOnboarding.ts) and holds the modal until the
// arm is known. Evaluated once per signed-in session: an onboarding arm must
// not re-roll on a flag refetch mid-flow. Do NOT add this to the eager fan-out.
export const planReadyStartLessonRequested = createEvent();
const $planReadyStartLessonRequested = createStore(false).on(planReadyStartLessonRequested, () => true);

const resolvePlanReadyStartLessonArmFx = createEffect((client: FlagsClient | null): PlanReadyStartLessonArm => {
    if (!client) return "off";
    const variant = client.variant(PLAN_READY_START_LESSON_FLAG_KEY);
    const value: any = variant?.value ?? (variant as any)?.key ?? (variant as any)?.payload?.value;
    const normalized = typeof value === "string" ? value.trim().toLowerCase() : "";
    if (normalized === "test" || normalized === "treatment") return "test";
    if (normalized === "control") return "control";
    return "off";
});

// Same server-side-events rationale as roadmaps_for_onboardings_variant above:
// cancellations and renewals are read from backend events, so mirror the arm
// for both groups as a user property.
const setPlanReadyStartLessonUserPropFx = createEffect((arm: PlanReadyStartLessonArm) => {
    if (typeof window === "undefined") return;
    AnalyticsService.setUserProps({ plan_ready_start_lesson_variant: arm });
});

sample({
    clock: [$planReadyStartLessonRequested.updates, $identityFlagsReady.updates],
    source: {
        client: $experimentClient,
        ready: $identityFlagsReady,
        flagsLoaded: $flagsLoaded,
        requested: $planReadyStartLessonRequested,
        arm: $planReadyStartLessonArm
    },
    // With a client, wait for the signed-in fetch (bucketing is by distinct_id);
    // without one $identityFlagsReady never flips, so release on $flagsLoaded
    // (set by initializeExperiment(null)) and resolve to "off".
    filter: ({ client, ready, flagsLoaded, requested, arm }) =>
        requested && arm === null && (client ? ready : flagsLoaded),
    fn: ({ client }) => client,
    target: resolvePlanReadyStartLessonArmFx
});

sample({
    clock: resolvePlanReadyStartLessonArmFx.doneData,
    target: $planReadyStartLessonArm
});

// The product onboarding waits for this store before opening — a throwing
// provider must not strand it.
sample({
    clock: resolvePlanReadyStartLessonArmFx.fail,
    fn: (): PlanReadyStartLessonArm => "off",
    target: $planReadyStartLessonArm
});

sample({
    clock: resolvePlanReadyStartLessonArmFx.doneData,
    filter: (arm) => arm !== "off",
    target: setPlanReadyStartLessonUserPropFx
});

const APPLE_START_PAGE_VARIANT_KEY = "apple_start_page_variant";

const computeAppleStartPageVariantFx = createEffect((client: FlagsClient | null) => {
    if (typeof window === "undefined") return null as string | null;

    const storedVariant = sessionStorage.getItem(APPLE_START_PAGE_VARIANT_KEY);
    if (storedVariant === "1" || storedVariant === "2" || storedVariant === "3") {
        return storedVariant;
    }

    if (!client) return null as string | null;
    const variant = client.variant("apple_start_page_variant");
    const value: any = variant?.value ?? (variant as any)?.payload?.value;
    if (typeof value === "string") {
        const normalized = value.trim();
        if (normalized === "1" || normalized === "2" || normalized === "3") {
            sessionStorage.setItem(APPLE_START_PAGE_VARIANT_KEY, normalized);
            return normalized;
        }
    }
    return null;
});

const APPLE_START_PAGE_DESIGN_VARIANTS: ReadonlySet<AppleStartPageDesignVariant> = new Set(["control", "test"]);

const computeAppleStartPageDesignVariantFx = createEffect((client: FlagsClient | null): AppleStartPageDesignVariant => {
    if (!client) return DEFAULT_APPLE_START_PAGE_DESIGN_VARIANT;

    const variant = client.variant("apple_start_page");
    const value: any = variant?.value ?? (variant as any)?.key ?? (variant as any)?.payload?.value;
    const normalized = typeof value === "string" ? value.trim().toLowerCase() : "";

    if (APPLE_START_PAGE_DESIGN_VARIANTS.has(normalized as AppleStartPageDesignVariant)) {
        return normalized as AppleStartPageDesignVariant;
    }

    return DEFAULT_APPLE_START_PAGE_DESIGN_VARIANT;
});

const computeRecordingOnInterviewPayloadFx = createEffect((client: FlagsClient | null) => {
    if (!client) return null as RecordingOnInterviewPayload | null;

    try {
        const variant = client.variant("recording_on_interview");
        if (!variant) return null;

        if (parseBooleanLike(variant?.value) !== true) return null;

        const payload: any = (variant as any)?.payload;
        const rawLessonRating: any = payload?.lesson_rating ?? payload?.lessonRating;

        const normalizeRatings = (val: any): number[] | null => {
            if (Array.isArray(val)) {
                const nums = val
                    .map((v) => (typeof v === "string" ? Number(v) : v))
                    .filter((v) => typeof v === "number" && Number.isFinite(v));
                return nums.length ? nums : null;
            }
            if (typeof val === "number") return Number.isFinite(val) ? [val] : null;
            if (typeof val === "string") {
                const n = Number(val);
                return Number.isFinite(n) ? [n] : null;
            }
            return null;
        };

        const lessonRatings = normalizeRatings(rawLessonRating);

        if (!lessonRatings || lessonRatings.length === 0) return null;

        const version: RecordingOnInterviewVersion = payload?.version === "call" ? "call" : "ai";

        // Per-version links (link_call / link_ai) let the payload hold both
        // booking URLs at once, so flipping `version` doesn't require swapping
        // `link` too; `link` stays as the fallback for either version.
        const versionLink: any = version === "call" ? payload?.link_call : payload?.link_ai;
        const link: any = typeof versionLink === "string" && versionLink.trim() ? versionLink : payload?.link;

        if (typeof link !== "string" || !link.trim()) return null;

        return {
            lesson_rating: lessonRatings.length === 1 ? lessonRatings[0] : lessonRatings,
            link: link.trim(),
            version
        };
    } catch {
        return null;
    }
});

const computeRequestIosReviewFx = createEffect((client: FlagsClient | null) => {
    if (!client) return null;

    try {
        const variant = client.variant("request_ios_review");
        if (!variant) return null;

        if (parseBooleanLike(variant?.value) !== true) return null;

        const payload: any = (variant as any)?.payload;
        const lessonId = payload?.lesson_id;

        if (typeof lessonId !== "number") return null;

        return { lesson_id: lessonId } as RequestIosReviewPayload;
    } catch {
        return null;
    }
});

const computePushNotificationTriggerLessonIdsFx = createEffect((client: FlagsClient | null) => {
    if (!client) return null as string | null;
    const variant = client.variant("push_notification_trigger_lesson_ids");
    const value: any = variant?.value ?? (variant as any)?.payload?.value ?? (variant as any)?.payload;
    if (typeof value === "string") {
        const normalized = value.trim();
        if (normalized.length > 0) return normalized;
    }
    if (typeof value === "number") return String(value);
    return null;
});

const setLessonsMapFx = createEffect((map: LessonsMapEdge[]) => {
    AnalyticsService.setLessonsMap(map);
});

const setLessonsRemapHeaderFx = createEffect((map: LessonsMapEdge[]) => {
    setLessonsRemap(map);
});

// Analytics map and X-Course-Remap header in ONE effect, so both are applied
// before $coursesMap updates below — consumers refetch course lists on that
// update and must never fetch with the previous remap header still set.
const applyCoursesRemapFx = createEffect((map: CoursesMapEdge[]) => {
    AnalyticsService.setCoursesMap(map);
    setCoursesRemap(map);
    return map;
});

export const roadmapCertificateEnabledChanged = createEvent<boolean>();
export const onboardingChallengeRedirectEnabledChanged = createEvent<boolean>();
export const trustPilotEnabledChanged = createEvent<boolean>();
export const unsubscribeStep4EnabledChanged = createEvent<boolean>();
export const unsubscribeStep5DiscountOfferEnabledChanged = createEvent<boolean>();
export const appleStartPageVariantChanged = createEvent<string>();
export const appleStartPageFlagsReadyChanged = createEvent<boolean>();
export const productOnboardingVariantChanged = createEvent<ProductOnboardingVariantPayload>();


$roadmapCertificateEnabled.on(roadmapCertificateEnabledChanged, (_, v) => v);
$onboardingChallengeRedirectEnabled.on(onboardingChallengeRedirectEnabledChanged, (_, v) => v);
$trustPilotEnabled.on(trustPilotEnabledChanged, (_, v) => v);
$unsubscribeStep4Enabled.on(unsubscribeStep4EnabledChanged, (_, v) => v);
$unsubscribeStep5DiscountOfferEnabled.on(unsubscribeStep5DiscountOfferEnabledChanged, (_, v) => v);
$appleStartPageVariant.on(appleStartPageVariantChanged, (_, v) => v);
$productOnboardingVariant.on(productOnboardingVariantChanged, (_, v) => v);


sample({
    clock: [initializeExperiment, refreshFlags],
    source: $experimentClient,
    filter: (client) => !!client,
    target: fetchFlagsFx
});

// Only after a completed fetch attempt for the current identity — never on
// bare init: client.variant() would serve (and auto-track $exposure for) the
// previous visit's cached variants, replaying stale variants after every
// rollout change (the landing_variant / course_page_redesign control tails).
// `.finally` (not `.doneData`) so a failed fetch still resolves flags from the
// SDK's persisted cache — the intended offline fallback.
sample({
    clock: fetchFlagsFx.finally,
    source: $experimentClient,
    target: [
        computeRoadmapCertificateEnabledFx,
        computeGamificationEnabledFx,
        computeDarkModeEnabledFx,
        computeOnboardingChallengeRedirectEnabledFx,
        computeTrustPilotEnabledFx,
        computeLessonFeedbackStarsEnabledFx,
        computeUnsubscribeStep4EnabledFx,
        computeUnsubscribeStep5DiscountOfferEnabledFx,
        computeLessonsMapFx,
        computeAiToolsUpsellVariantFx,
        computeAppleStartPageVariantFx,
        computeAppleStartPageDesignVariantFx,
        computeRecordingOnInterviewPayloadFx,
        computeRequestIosReviewFx,
        computeProductOnboardingVariantFx,
        computePushNotificationTriggerLessonIdsFx,
        computeNotificationsSettingsEnabledFx,
        computeStreakFlowFx,
        computeLandingV4VariantFx,
        computeSubscriptionPageVariantFx,
        computeDownloadInvoiceEnabledFx,
        // IMPORTANT: must be last (see `signalFlagsReadyFx` comment above)
        signalFlagsReadyFx
    ]
});

sample({
    clock: computeRoadmapCertificateEnabledFx.doneData,
    target: $roadmapCertificateEnabled
});

sample({
    clock: computeGamificationEnabledFx.doneData,
    target: $gamificationEnabled
});

sample({
    clock: computeDarkModeEnabledFx.doneData,
    target: $darkModeEnabled
});

sample({
    clock: computeOnboardingChallengeRedirectEnabledFx.doneData,
    target: $onboardingChallengeRedirectEnabled
});

sample({
    clock: computeTrustPilotEnabledFx.doneData,
    target: $trustPilotEnabled
});

sample({
    clock: computeLessonFeedbackStarsEnabledFx.doneData,
    target: $lessonFeedbackStarsEnabled
});

sample({
    clock: computeUnsubscribeStep4EnabledFx.doneData,
    target: $unsubscribeStep4Enabled
});

sample({
    clock: computeUnsubscribeStep5DiscountOfferEnabledFx.doneData,
    target: $unsubscribeStep5DiscountOfferEnabled
});

sample({
    clock: computeLessonsMapFx.doneData,
    target: $lessonsMap
});

sample({
    clock: computeLessonsMapFx.doneData,
    target: setLessonsMapFx
});

sample({
    clock: computeLessonsMapFx.doneData,
    target: setLessonsRemapHeaderFx
});

sample({
    clock: computeCoursesMapFx.doneData,
    target: applyCoursesRemapFx
});

// Reference-stable when the payload is unchanged, so $coursesMap.updates fires
// only on real remap changes — that update is the consumers' refetch signal.
$coursesMap.on(applyCoursesRemapFx.doneData, (prev, next) =>
    JSON.stringify(prev) === JSON.stringify(next) ? prev : next
);

sample({
    clock: computeAiToolsUpsellVariantFx.doneData,
    fn: (v) => v ?? null,
    target: $aiToolsUpsellVariant
});

sample({
    clock: computeAiToolsUpsellVariantFx.doneData,
    filter: (v): v is string => typeof v === "string" && v.length > 0,
    target: setAiToolsUpsellVariantUserPropFx
});

sample({
    clock: computeAppleStartPageVariantFx.doneData,
    filter: (v): v is string => typeof v === "string" && v.length > 0,
    target: $appleStartPageVariant
});

sample({
    clock: computeAppleStartPageDesignVariantFx.doneData,
    target: $appleStartPageDesignVariant
});

sample({
    clock: computeRecordingOnInterviewPayloadFx.doneData,
    target: $recordingOnInterview
});

sample({
    clock: computeRequestIosReviewFx.doneData,
    target: $requestIosReview
});

sample({
    clock: computeBackToLearningVariantFx.doneData,
    target: $backToLearningVariant
});

sample({
    clock: computeNotificationsSettingsEnabledFx.doneData,
    fn: (v) => v === true,
    target: $notificationsSettingsEnabled
});

sample({
    clock: computeProductOnboardingVariantFx.doneData,
    fn: (data) => data ?? DEFAULT_PRODUCT_ONBOARDING_VARIANT,
    target: $productOnboardingVariant
});

sample({
    clock: computePushNotificationTriggerLessonIdsFx.doneData,
    target: $pushNotificationTriggerLessonIds
});

sample({
    clock: computeStreakFlowFx.doneData,
    target: $streakFlow
});

sample({
    clock: computeLandingV4VariantFx.doneData,
    target: $landingV4Variant
});

sample({
    clock: computeSubscriptionPageVariantFx.doneData,
    target: $subscriptionPageVariant
});

sample({
    clock: computeDownloadInvoiceEnabledFx.doneData,
    fn: (v) => v === true,
    target: $downloadInvoiceEnabled
});

// Two flags write the lessons remap: `lessons_map` (above) and this one, whose payload carries
// its own lessonsMap. This one sits later in the compute fan-out, so it lands last — and since
// an empty array is not nullish, the old `lessonsMap ?? DEFAULT` fallback let a variant with no
// array payload silently wipe whatever `lessons_map` had just set. Override only with pairs it
// actually carries.
sample({
    clock: computeProductOnboardingVariantFx.doneData,
    filter: (data): data is ProductOnboardingVariantPayload =>
        data !== null && Array.isArray(data.lessonsMap) && data.lessonsMap.length > 0,
    fn: (data) => (data as ProductOnboardingVariantPayload).lessonsMap,
    target: [setLessonsMapFx, setLessonsRemapHeaderFx]
});

sample({
    clock: signalFlagsReadyFx.finally,
    source: fetchFlagsFx.pending,
    filter: (pending) => !pending,
    fn: () => true,
    target: [$flagsLoaded, $appleStartPageFlagsReady]
});

// No experiment client (missing API key): flags can never load — release
// consumers waiting on the ready stores instead of hanging them forever.
sample({
    clock: initializeExperiment,
    filter: (client) => !client,
    fn: () => true,
    target: [$flagsLoaded, $appleStartPageFlagsReady, $identityFlagsSettled]
});

// The same release on every refresh (identify after sign-in or session restore): the refresh
// resets the ready stores above, and with no client there is no fetch to set them again, which
// left the landing blank and /home on its loader after a logout/login in one session.
sample({
    clock: refreshFlags,
    source: $experimentClient,
    filter: (client) => !client,
    fn: () => true,
    target: [$flagsLoaded, $appleStartPageFlagsReady, $identityFlagsSettled]
});

sample({
    clock: signalFlagsReadyFx.finally,
    source: { identified: $userIdentified, pending: fetchFlagsFx.pending },
    filter: ({ identified, pending }) => identified && !pending,
    fn: () => true,
    target: $identityFlagsReady
});

// Latch AFTER the remap side effects ran (applyCoursesRemapFx sets the
// X-Course-Remap header synchronously in its body), so a consumer fetching
// course data on this signal can never race a stale header. The logout-time
// apply is excluded via $userIdentified.
sample({
    clock: applyCoursesRemapFx.done,
    source: $userIdentified,
    filter: (identified) => identified,
    fn: () => true,
    target: $identityFlagsSettled
});

// Signed-in-only flags (see $identityFlagsReady above). Deliberately NOT in
// the eager fan-out: they must be evaluated only under the signed-in identity.
sample({
    clock: $identityFlagsReady.updates,
    source: $experimentClient,
    filter: (client, ready) => ready && !!client,
    target: [computeBackToLearningVariantFx, computeCoursesMapFx]
});

// Legal A/B (features/legal-experiment): the funnel bucketed the person into
// one legal_<onboarding> flag by the legal_cohort person property; the app
// evaluates that same flag under the signed-in identity (one person after
// identify) and applies its payload. Found in the exposure-free snapshot, then
// variant() tracks the exposure. Null = unresolved or not in the experiment —
// the consumers then fall back to the purchase onboarding (see
// features/legal-experiment/model).
const $legalFlag = createStore<LegalFlag | null>(null);

const resolveLegalFlagFx = createEffect((client: FlagsClient | null): LegalFlag | null => {
    if (!client) return null;
    const key = findLegalFlagKey(client.all());
    if (!key) return null;
    const variant = client.variant(key);
    return variant?.value ? { key, variant: variant.value, payload: variant.payload } : null;
});

// Mirrored as person properties so backend-fired events (cancellations,
// renewals, restores from webhooks) stay segmentable by arm.
const setLegalUserPropsFx = createEffect((flag: LegalFlag | null) => {
    if (!flag || typeof window === "undefined") return;
    AnalyticsService.setUserProps({ legal_experiment: flag.key, legal_variant: flag.variant });
});

sample({
    clock: $identityFlagsReady.updates,
    source: $experimentClient,
    filter: (client, ready) => ready && !!client,
    target: resolveLegalFlagFx
});

sample({
    clock: resolveLegalFlagFx.doneData,
    target: $legalFlag
});

sample({
    clock: resolveLegalFlagFx.doneData,
    filter: (flag) => !!flag,
    target: setLegalUserPropsFx
});

const contract = new ModelContract(
    {
        $experimentClient,
        $roadmapCertificateEnabled,
        $gamificationEnabled,
        $darkModeEnabled,
        $onboardingChallengeRedirectEnabled,
        $trustPilotEnabled,
        $lessonFeedbackStarsEnabled,
        $lessonsMap,
        $coursesMap,
        $unsubscribeStep4Enabled,
        $unsubscribeStep5DiscountOfferEnabled,
        $unsubscribeOffersVariant,
        $aiToolsUpsellVariant,
        $appleStartPageVariant,
        $appleStartPageDesignVariant,
        $recordingOnInterview,
        $requestIosReview,
        $productOnboardingVariant,
        $appleStartPageFlagsReady,
        $pushNotificationTriggerLessonIds,
        $backToLearningVariant,
        $notificationsSettingsEnabled,
        $streakFlow,
        $landingV4Variant,
        $subscriptionPageVariant,
        $roadmapOnboardingExperimentArm,
        $planReadyStartLessonArm,
        $appTourEnabled,
        $aiToolsProfessionSetEnabled,
        $flagsLoaded,
        $identityFlagsReady,
        $identityFlagsSettled,
        $downloadInvoiceEnabled,
        $localizeEnabled,
        $legalFlag
    },
    {
        refreshFlags,
        roadmapOnboardingExperimentRequested,
        appTourFlagRequested,
        unsubscribeOffersVariantRequested,
        aiToolsProfessionSetRequested,
        planReadyStartLessonRequested
    },
    // Exposed so the unsubscribe wizard can track "screen shown" of the offer
    // steps with the resolved variant exactly once per display (store updates
    // would be skipped when the next step resolves to the same variant).
    { resolveUnsubscribeOffersVariantFx },
    {}
);

export type IAmplitudeFlagsContract = typeof contract;

export class AmplitudeFlagsModule extends ModuleDescription<IAmplitudeFlagsContract> {
    constructor(public auth: IUserAuthContract) {
        super();
    }
    getDefaultContract() {
        return contract;
    }

    run(): void {
        // Attached before the refetch sample below so the fetch triggered by
        // the same event already observes $userIdentified === true. `.finally`
        // (not `.done`): even if the identify call rejects, the identity WAS
        // set synchronously first — treating it as "not identified" would
        // strand every consumer gated on $identityFlagsSettled behind an
        // eternal loader.
        $userIdentified
            .on(this.auth.effects.setUserIdInAmplitudeFx.finally, () => true)
            .reset(this.auth.events.logoutStudent);
        $identityFlagsReady.reset(this.auth.events.logoutStudent);
        $identityFlagsSettled.reset(this.auth.events.logoutStudent);

        sample({
            clock: this.auth.effects.setUserIdInAmplitudeFx.finally,
            target: refreshFlags
        });

        // Don't leak the previous student's signed-in flag state across logout.
        $backToLearningVariant.reset(this.auth.events.logoutStudent);
        $planReadyStartLessonArm.reset(this.auth.events.logoutStudent);
        $planReadyStartLessonRequested.reset(this.auth.events.logoutStudent);
        $legalFlag.reset(this.auth.events.logoutStudent);
        sample({
            clock: this.auth.events.logoutStudent,
            fn: () => [] as CoursesMapEdge[],
            target: applyCoursesRemapFx
        });
    }
}

export const $roadmapCertificateEnabledFlag = $roadmapCertificateEnabled;
export const $gamificationEnabledFlag = $gamificationEnabled;
export const $darkModeEnabledFlag = $darkModeEnabled;
export const $onboardingChallengeRedirectEnabledFlag = $onboardingChallengeRedirectEnabled;
export const $trustPilotEnabledFlag = $trustPilotEnabled;
export const $lessonFeedbackStarsEnabledFlag = $lessonFeedbackStarsEnabled;
export const $lessonsMapFlag = $lessonsMap;
export const $coursesMapFlag = $coursesMap;
export const $unsubscribeStep4EnabledFlag = $unsubscribeStep4Enabled;
export const $unsubscribeStep5DiscountOfferEnabledFlag = $unsubscribeStep5DiscountOfferEnabled;
export const $unsubscribeOffersVariantFlag = $unsubscribeOffersVariant;
export const $aiToolsUpsellVariantFlag = $aiToolsUpsellVariant;
export const $appleStartPageVariantFlag = $appleStartPageVariant;
export const $appleStartPageDesignVariantFlag = $appleStartPageDesignVariant;
export const $appleStartPageFlagsReadyFlag = $appleStartPageFlagsReady;
export const $recordingOnInterviewFlag = $recordingOnInterview;
export const $requestIosReviewFlag = $requestIosReview;
export const $productOnboardingVariantFlag = $productOnboardingVariant;
export const $pushNotificationTriggerLessonIdsFlag = $pushNotificationTriggerLessonIds;
export const $backToLearningVariantFlag = $backToLearningVariant;
export const $notificationsSettingsEnabledFlag = $notificationsSettingsEnabled;
export const $streakFlowFlag = $streakFlow;
export const $landingV4VariantFlag = $landingV4Variant;
export const $subscriptionPageVariantFlag = $subscriptionPageVariant;
export const $roadmapOnboardingExperimentArmFlag = $roadmapOnboardingExperimentArm;
export const $planReadyStartLessonArmFlag = $planReadyStartLessonArm;
export const $appTourEnabledFlag = $appTourEnabled;
export const $flagsLoadedFlag = $flagsLoaded;
export const $identityFlagsReadyFlag = $identityFlagsReady;
export const $identityFlagsSettledFlag = $identityFlagsSettled;
export const $downloadInvoiceEnabledFlag = $downloadInvoiceEnabled;
export const $localizeEnabledFlag = $localizeEnabled;
